import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Button, Container, Table, Spinner, Alert, Row, Col, Card, InputGroup, Modal, Form, Collapse } from 'react-bootstrap';
import { getExpenses, deleteExpense, getSuppliers, getExpenseCategories } from '../services/api';
import { buildExpenseCategoryOptions, formatExpenseCategory } from '../utils/expenseCategories';
import api from '../services/api';
import { trackAction } from '../services/analytics';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Pagination from '../components/Pagination';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import AuthContext from '../context/AuthContext';
import PageSizeSelector from '../components/PageSizeSelector';
import { FaPrint, FaShareAlt, FaCashRegister, FaEdit, FaTrash, FaTable, FaTh } from 'react-icons/fa';
import { BsWhatsapp, BsClipboardCheck, BsBoxArrowUpRight, BsShareFill } from 'react-icons/bs';
import CreateExpenseModal from '../components/CreateExpenseModal';
import ShareModal from '../components/ShareModal';
import FinancialSummaryCard from '../components/FinancialSummaryCard';
import useCashRegisterAutoOpen from '../hooks/useCashRegisterAutoOpen';
import OpenCashRegisterModal from '../components/OpenCashRegisterModal';
import CloseCashRegisterModal from '../components/CloseCashRegisterModal';


const ExpensesPage = () => {
    const { user, business } = useContext(AuthContext); // Get business from context
    const { hasPermission } = usePermissions(user);
    const { t } = useTranslation();
    const [expenses, setExpenses] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [expenseCategories, setExpenseCategories] = useState([]);

    // Check subscription status
    const isSubscriptionActive = ['active', 'trialing'].includes(business?.subscription_status);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showCloseCashModal, setShowCloseCashModal] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);

    // Share Modal State
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareableLink, setShareableLink] = useState('');
    const [expenseToShare, setExpenseToShare] = useState(null);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('expensesViewMode') || 'table');

    const toggleViewMode = (mode) => {
        setViewMode(mode);
        localStorage.setItem('expensesViewMode', mode);
    };

    const getLocalISODate = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const today = getLocalISODate();

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalExpensesCount, setTotalExpensesCount] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalSalesAmount, setTotalSalesAmount] = useState(0);
    const [totalCollectedAmount, setTotalCollectedAmount] = useState(0);

    // Filters
    const [filters, setFilters] = useState({
        category: '',
        startDate: today,
        endDate: today
    });
    // Active filters
    const [activeFilters, setActiveFilters] = useState({
        category: '',
        startDate: today,
        endDate: today
    });
    const [showFilters, setShowFilters] = useState(false);

    // Cash Register Auto-Open Hook
    const hasExistingTransactions = expenses.length > 0;
    const {
        isCashRegisterOpen,
        canOpenAStation,
        cashRegister,
        openModal: openCashRegisterModal,
        setPendingTransactionCallback,
        modalProps: cashModalProps,
        refetchCashStatus
    } = useCashRegisterAutoOpen({
        hasExistingTransactions,
        hasPermission: hasPermission(PERMISSIONS.VIEW_CASH_REGISTER)
    });

    const fetchExpenses = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                limit: pageSize,
                category: activeFilters.category || undefined,
                startDate: activeFilters.startDate || undefined,
                endDate: activeFilters.endDate || undefined
            };
            const response = await getExpenses(params);
            setExpenses(response.data.expenses);
            setTotalPages(response.data.totalPages);
            setTotalExpensesCount(response.data.totalExpenses);
            setTotalAmount(response.data.totalAmount);
            setError('');
        } catch (error) {
            console.error(error);
            if (error.response && error.response.status === 403) {
                setError(error.response.data.message || t('expensesPage.toasts.permissionError'));
            } else {
                toast.error(t('expensesPage.toasts.fetchFailed'));
            }
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, activeFilters, t]);

    // Fetch sales totals to display in the FinancialSummaryCard
    const fetchSalesTotals = useCallback(async () => {
        try {
            const params = new URLSearchParams({
                startDate: activeFilters.startDate || '',
                endDate: activeFilters.endDate || '',
                page: 1,
                limit: 1,
                dateFilterType: 'saleDate'
            });
            const response = await api.get(`/sales?${params}`);
            setTotalSalesAmount(response.data.totalSalesAmount || 0);
            setTotalCollectedAmount(response.data.totalCollectedAmount || 0);
        } catch (error) {
            console.error('Failed to fetch sales totals', error);
        }
    }, [activeFilters.startDate, activeFilters.endDate]);

    const fetchSuppliers = async () => {
        try {
            const response = await getSuppliers({ pagination: false });
            setSuppliers(response.data);
        } catch (error) {
            console.error("Failed to load suppliers", error);
        }
    };

    const fetchExpenseCategories = useCallback(async () => {
        try {
            const response = await getExpenseCategories();
            setExpenseCategories(response.data);
        } catch (error) {
            console.error("Failed to load expense categories", error);
        }
    }, []);

    useEffect(() => {
        if (hasPermission(PERMISSIONS.VIEW_EXPENSES)) {
            fetchExpenses();
            fetchSuppliers();
            fetchExpenseCategories();
            fetchSalesTotals();
        } else {
            setLoading(false);
            setError(t('expensesPage.toasts.permissionError'));
            setTotalAmount(0);
        }
    }, [fetchExpenses, fetchExpenseCategories, fetchSalesTotals, hasPermission, t]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const applyFilters = () => {
        setCurrentPage(1);
        setActiveFilters(filters);
    };

    const clearFilters = () => {
        const initialFilters = {
            category: '',
            startDate: today,
            endDate: today
        };
        setFilters(initialFilters);
        setActiveFilters(initialFilters);
        setCurrentPage(1);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditingExpense(null);
    };

    const handleCreateClick = () => {
        setEditingExpense(null);
        setShowModal(true);
    };

    const handleEditClick = (expense) => {
        setEditingExpense(expense);
        setShowModal(true);
    };

    const handleDelete = async (expenseId) => {
        const result = await Swal.fire({
            title: t('areYouSure'),
            text: t('expensesPage.toasts.deleteConfirm'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ev-action-danger-bg)',
            cancelButtonColor: 'var(--ev-bg-subtle)',
            confirmButtonText: t('expensesPage.actions.delete'),
            cancelButtonText: t('cancel')
        });

        if (result.isConfirmed) {
            const toastId = toast.loading(t('expensesPage.toasts.deleting'));
            try {
                await deleteExpense(expenseId);
                fetchExpenses();
                trackAction('expense_deleted');
                toast.success(t('expensesPage.toasts.deleted'), { id: toastId });
            } catch (error) {
                toast.error(t('expensesPage.toasts.deleteFailed'), { id: toastId });
            }
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handlePrintClick = (expense) => {
        const link = `${window.location.origin}/expense-receipt/${expense.public_id}?print=true`;
        window.open(link, '_blank');
    };

    const handleShareClick = (expense) => {
        const link = `${window.location.origin}/expense-receipt/${expense.public_id}`;
        setShareableLink(link);
        setExpenseToShare(expense);
        setShowShareModal(true);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(shareableLink);
        toast.success(t('toast.linkCopied'));
    };

    return (
        <Container className="mt-4">
            <Row className="align-items-center mb-4">
                <Col md={4}>
                    <h2>{t('expensesPage.title')}</h2>
                    <p className="text-muted mb-0">{t('expensesPage.subtitle')}</p>
                </Col>
                <Col md={4}>
                    {hasPermission(PERMISSIONS.VIEW_FINANCIAL_SUMMARY) && (
                        <FinancialSummaryCard
                            totalSales={totalSalesAmount}
                            totalCollected={totalCollectedAmount}
                            totalExpenses={totalAmount}
                            formatCurrency={(value) => `$${value.toLocaleString()}`}
                        />
                    )}
                </Col>
                <Col md={4} className="text-end d-none d-md-block">
                    {/* Cash Register Buttons */}
                    {isCashRegisterOpen ? (
                        hasPermission(PERMISSIONS.CLOSE_CASH_REGISTER) && (
                            <Button
                                variant="dark"
                                onClick={() => setShowCloseCashModal(true)}
                                className="me-2"
                            >
                                <FaCashRegister className="me-2" />
                                {t('cashRegisterPage.salesPageIntegration.closeCashRegister')}
                            </Button>
                        )
                    ) : (
                        hasPermission(PERMISSIONS.OPEN_CASH_REGISTER) && canOpenAStation && (
                            <Button
                                variant="success"
                                onClick={() => openCashRegisterModal()}
                                className="me-2"
                            >
                                <FaCashRegister className="me-2" />
                                {t('cashRegisterPage.salesPageIntegration.openCashRegister')}
                            </Button>
                        )
                    )}

                    {hasPermission(PERMISSIONS.CREATE_EXPENSES) && (
                        <div title={!isSubscriptionActive ? t('subscriptionRequired') : !business?.plan_features?.expenses ? t('common.featureNotAvailable') : ''} className="d-inline-block">
                            <Button
                                variant="danger"
                                onClick={handleCreateClick}
                                disabled={!isSubscriptionActive || !business?.plan_features?.expenses}
                            >
                                {t('expensesPage.newExpenseButton')}
                            </Button>
                        </div>
                    )}
                </Col>
            </Row>

            {/* Mobile Fixed Bottom Buttons */}
            <div className="d-md-none position-fixed bottom-0 start-0 end-0 px-3 pt-3 bg-white shadow-lg" style={{ zIndex: 1000, borderTop: '1px solid #dee2e6', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
                <div className="d-flex gap-2">
                    {/* Cash Register Buttons Mobile */}
                    {isCashRegisterOpen ? (
                        hasPermission(PERMISSIONS.CLOSE_CASH_REGISTER) && (
                            <Button
                                variant="dark"
                                onClick={() => setShowCloseCashModal(true)}
                                className="flex-grow-1"
                            >
                                <FaCashRegister /> {t('cashRegisterPage.salesPageIntegration.closeCashRegister')}
                            </Button>
                        )
                    ) : (
                        hasPermission(PERMISSIONS.OPEN_CASH_REGISTER) && canOpenAStation && (
                            <Button
                                variant="success"
                                onClick={() => openCashRegisterModal()}
                                className="flex-grow-1"
                            >
                                <FaCashRegister /> {t('cashRegisterPage.salesPageIntegration.openCashRegister')}
                            </Button>
                        )
                    )}
                    {hasPermission(PERMISSIONS.CREATE_EXPENSES) && (
                        <Button
                            variant="danger"
                            onClick={handleCreateClick}
                            disabled={!isSubscriptionActive || !business?.plan_features?.expenses}
                            className="flex-grow-1"
                        >
                            {t('expensesPage.newExpenseButton')}
                        </Button>
                    )}
                </div>
            </div>

            <div className="d-md-none mb-3">
                <Button variant="link" onClick={() => setShowFilters(!showFilters)} className="p-0 text-decoration-none">
                    {showFilters ? t('expensesPage.filter.hideFilters', 'Ocultar filtros') : t('expensesPage.filter.viewFilters', 'Ver filtros')}
                </Button>
            </div>

            <Collapse in={showFilters || window.innerWidth >= 768}>
                <div id="expenses-filters">
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Body>
                            <Form>
                                <Row className="align-items-end">
                                    <Col md={3}>
                                        <Form.Group className="mb-2 mb-md-0">
                                            <Form.Label>{t('expensesPage.filter.allCategories')}</Form.Label>
                                            <Form.Select name="category" value={filters.category} onChange={handleFilterChange}>
                                                <option value="">{t('expensesPage.filter.allCategories')}</option>
                                                {buildExpenseCategoryOptions(expenseCategories, t).map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-2 mb-md-0">
                                            <Form.Label>{t('expensesPage.filter.startDate')}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="startDate"
                                                value={filters.startDate}
                                                onChange={handleFilterChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-2 mb-md-0">
                                            <Form.Label>{t('expensesPage.filter.endDate')}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="endDate"
                                                value={filters.endDate}
                                                onChange={handleFilterChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3} className="d-flex mt-2 mt-md-0">
                                        <Button
                                            variant="primary"
                                            onClick={applyFilters}
                                            className="me-2 flex-grow-1 flex-md-grow-0"
                                        >
                                            {t('expensesPage.filter.apply')}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={clearFilters}
                                            className="flex-grow-1 flex-md-grow-0"
                                        >
                                            {t('expensesPage.filter.clear')}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
            </Collapse>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* View Mode Toggle */}
            <div className="d-flex justify-content-end align-items-center mb-3">
                <div
                    className="sales-view-toggle"
                    role="group"
                    aria-label={t('expensesPage.viewToggle.label', 'Cambiar vista')}
                >
                    <button
                        id="expenses-view-table"
                        className={`sales-view-toggle__btn${viewMode === 'table' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('table')}
                        aria-pressed={viewMode === 'table'}
                        title={t('expensesPage.viewToggle.tableView', 'Vista tabla')}
                        aria-label={t('expensesPage.viewToggle.tableView', 'Vista tabla')}
                    >
                        <FaTable aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('expensesPage.viewToggle.tableView', 'Tabla')}</span>
                    </button>
                    <button
                        id="expenses-view-cards"
                        className={`sales-view-toggle__btn${viewMode === 'cards' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('cards')}
                        aria-pressed={viewMode === 'cards'}
                        title={t('expensesPage.viewToggle.cardsView', 'Vista tarjetas')}
                        aria-label={t('expensesPage.viewToggle.cardsView', 'Vista tarjetas')}
                    >
                        <FaTh aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('expensesPage.viewToggle.cardsView', 'Tarjetas')}</span>
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
                <>
                    {viewMode === 'table' ? (
                    <div className="modern-table-wrapper mt-1">
                        <Table hover responsive className="modern-table">
                            <thead>
                                <tr>
                                    <th>{t('expensesPage.table.date')}</th>
                                    <th>{t('expensesPage.table.category')}</th>
                                    <th>Proveedor</th>
                                    <th>{t('expensesPage.table.description')}</th>
                                    <th>{t('expensesPage.table.paymentMethod')}</th>
                                    <th>{t('expensesPage.table.registeredBy', 'Registrado por')}</th>
                                    <th>{t('expensesPage.table.cashRegister', 'Caja')}</th>
                                    <th className="text-end">{t('expensesPage.table.amount')}</th>
                                    <th className="text-center">{t('expensesPage.table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.length > 0 ? expenses.map(expense => (
                                    <tr key={expense.expense_id}>
                                        <td>{new Date(expense.date).toLocaleDateString('es-CO', { timeZone: 'UTC' })}</td>
                                        <td>
                                            <span className="badge bg-light text-dark border p-2">{formatExpenseCategory(expense.category, t)}</span>
                                            {expense.is_deductible && <span className="badge bg-warning text-dark ms-1" title={t('expensesPage.deductibleTooltip')}>{t('expensesPage.deductible')}</span>}
                                            {expense.settlement_id && <span className="badge bg-success ms-1">{t('expensesPage.settled')}</span>}
                                        </td>
                                        <td>{expense.supplier_name_joined || expense.supplier_name || '-'}</td>
                                        <td>{expense.description}</td>
                                        <td>{t(`expensesPage.paymentMethods.${expense.payment_method}`) || expense.payment_method}</td>
                                        <td className="text-muted small">{expense.creator_name || '-'}</td>
                                        <td className="text-muted small">{expense.station_name || '-'}</td>
                                        <td className="text-end fw-bold">${parseFloat(expense.amount).toLocaleString()}</td>
                                        <td className="text-center">
                                            <div className="btn-actions">
                                                <Button variant="info" size="sm" onClick={() => handleShareClick(expense)} title="Copiar Enlace">
                                                    <FaShareAlt />
                                                </Button>
                                                <Button variant="outline-secondary" size="sm" onClick={() => handlePrintClick(expense)} title="Imprimir">
                                                    <FaPrint />
                                                </Button>
                                                {hasPermission(PERMISSIONS.EDIT_EXPENSES) && (
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => handleEditClick(expense)}
                                                        title={t('expensesPage.actions.edit')}
                                                        aria-label={t('expensesPage.actions.edit')}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                )}
                                                {hasPermission(PERMISSIONS.DELETE_EXPENSES) && (
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(expense.expense_id)}
                                                        title={t('expensesPage.actions.delete')}
                                                        aria-label={t('expensesPage.actions.delete')}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="9" className="text-center py-4 text-muted">{t('expensesPage.noExpenses')}</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                    ) : (
                        /* ── Cards view ── */
                        <div>
                            {expenses.length === 0 ? (
                                <div className="text-center py-5 text-muted">{t('expensesPage.noExpenses')}</div>
                            ) : (
                                <div className="sales-cards-grid">
                                    {expenses.map(expense => (
                                        <div key={expense.expense_id} className="sale-card">
                                            <div className="sale-card__header">
                                                <div className="sale-card__invoice">
                                                    <span className="sale-card__invoice-label">{new Date(expense.date).toLocaleDateString('es-CO', { timeZone: 'UTC' })}</span>
                                                </div>
                                                <span className="badge bg-light text-dark border sale-card__badge">{formatExpenseCategory(expense.category, t)}</span>
                                            </div>

                                            {(expense.is_deductible || expense.settlement_id) && (
                                                <div className="d-flex gap-1 flex-wrap">
                                                    {expense.is_deductible && (
                                                        <span className="badge bg-warning text-dark" title={t('expensesPage.deductibleTooltip')}>{t('expensesPage.deductible')}</span>
                                                    )}
                                                    {expense.settlement_id && (
                                                        <span className="badge bg-success">{t('expensesPage.settled')}</span>
                                                    )}
                                                </div>
                                            )}

                                            <div className="sale-card__client">
                                                <span className="sale-card__client-name">{expense.supplier_name_joined || expense.supplier_name || expense.description || '—'}</span>
                                                {expense.description && (expense.supplier_name_joined || expense.supplier_name) && (
                                                    <div className="text-muted small mt-1">{expense.description}</div>
                                                )}
                                            </div>

                                            <div className="sale-card__amounts">
                                                <div className="sale-card__amount-item">
                                                    <span className="sale-card__amount-label">{t('expensesPage.table.amount')}</span>
                                                    <span className="sale-card__amount-value sale-card__amount-value--total">${parseFloat(expense.amount).toLocaleString()}</span>
                                                </div>
                                                <div className="sale-card__amount-item">
                                                    <span className="sale-card__amount-label">{t('expensesPage.table.paymentMethod')}</span>
                                                    <span className="sale-card__amount-value">{t(`expensesPage.paymentMethods.${expense.payment_method}`) || expense.payment_method}</span>
                                                </div>
                                            </div>

                                            <div className="sale-card__dates">
                                                <div className="sale-card__date-item">
                                                    <span className="sale-card__date-label">{t('expensesPage.table.registeredBy', 'Registrado por')}</span>
                                                    <span className="sale-card__date-value">{expense.creator_name || '-'}</span>
                                                </div>
                                                <div className="sale-card__date-item">
                                                    <span className="sale-card__date-label">{t('expensesPage.table.cashRegister', 'Caja')}</span>
                                                    <span className="sale-card__date-value">{expense.station_name || '-'}</span>
                                                </div>
                                            </div>

                                            <div className="sale-card__actions btn-actions">
                                                <Button variant="info" size="sm" onClick={() => handleShareClick(expense)} title="Copiar Enlace">
                                                    <FaShareAlt />
                                                </Button>
                                                <Button variant="outline-secondary" size="sm" onClick={() => handlePrintClick(expense)} title="Imprimir">
                                                    <FaPrint />
                                                </Button>
                                                {hasPermission(PERMISSIONS.EDIT_EXPENSES) && (
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => handleEditClick(expense)}
                                                        title={t('expensesPage.actions.edit')}
                                                        aria-label={t('expensesPage.actions.edit')}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                )}
                                                {hasPermission(PERMISSIONS.DELETE_EXPENSES) && (
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(expense.expense_id)}
                                                        title={t('expensesPage.actions.delete')}
                                                        aria-label={t('expensesPage.actions.delete')}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                        <PageSizeSelector pageSize={pageSize} onPageSizeChange={handlePageSizeChange} />
                    </div>
                </>
            )}

            {/* Share Modal */}
            <ShareModal
                show={showShareModal}
                onHide={() => setShowShareModal(false)}
                shareableLink={shareableLink}
                message={expenseToShare ? `Comprobante de Egreso #${expenseToShare.expense_id} - ${formatExpenseCategory(expenseToShare.category, t)}. Valor: $${parseFloat(expenseToShare.amount).toLocaleString()}. Ver aquí: ${shareableLink}` : ''}
                title='Comprobante de Egreso'
                phoneNumber={null}
                onPrint={() => handlePrintClick(expenseToShare)}
                openButtonLabel="Abrir"
            />

            {/* Create/Edit Expense Modal */}
            <CreateExpenseModal
                show={showModal}
                onHide={handleModalClose}
                suppliers={suppliers}
                expenseCategories={expenseCategories}
                onCategoryCreated={fetchExpenseCategories}
                expenseToEdit={editingExpense}
                onExpenseCreated={fetchExpenses}
                onExpenseUpdated={fetchExpenses}
                business={business}
                isCashRegisterOpen={isCashRegisterOpen || !canOpenAStation}
                onOpenCashRegister={(options) => {
                    // Use the hook to handle pending expense callback
                    if (options?.pendingCallback) {
                        setPendingTransactionCallback(options.pendingCallback);
                    }
                    setShowModal(false);
                }}
            />

            {/* Open Cash Register Modal - using hook props */}
            <OpenCashRegisterModal {...cashModalProps} />

            {/* Close Cash Register Modal */}
            <CloseCashRegisterModal
                show={showCloseCashModal}
                onHide={() => setShowCloseCashModal(false)}
                cashRegister={cashRegister}
                onSuccess={() => {
                    refetchCashStatus();
                    // Toast is handled inside the modal
                }}
            />
        </Container>
    );
};

export default ExpensesPage;